export const BLANK_TYPE_OPTIONS = [
    { value: 'stele', label: 'Стела' },
    { value: 'base', label: 'Подставка' },
    { value: 'flower-bed', label: 'Цветник / поребрик' },
    { value: 'slab', label: 'Плита' },
    { value: 'table', label: 'Стол' },
    { value: 'bench', label: 'Скамейка' },
    { value: 'support', label: 'Опора / ножка' },
];

export const POLISHING_OPTIONS = [
    { value: 'one-side', label: '1 сторона' },
    { value: 'two-side', label: '2 стороны' },
    { value: 'three-side', label: '3 стороны' },
    { value: 'four-side', label: '4 стороны' },
    { value: 'five-side', label: '5 сторон' },
    { value: 'full-perimeter', label: 'Круговая' },
    { value: 'unpolished', label: 'Без полировки' },
];

export const PRICE_UNIT_OPTIONS = [
    { value: 'piece', label: 'за штуку' },
    { value: 'squareMeter', label: 'за м²' },
    { value: 'cubicMeter', label: 'за м³' },
];

export const createClientId = () => (
    `${Date.now()}-${Math.random().toString(16).slice(2)}`
);

export const toNumber = (value, fallback = 0) => {
    const prepared = String(value ?? '').replace(',', '.').trim();
    if (!prepared) return fallback;
    const number = Number(prepared);
    return Number.isFinite(number) ? number : fallback;
};

export const formatMoney = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0
        ? `${number.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`
        : '—';
};

export const formatDimensions = (value = {}) => {
    const height = Number(value.height || 0);
    const width = Number(value.width || 0);
    const thickness = Number(value.thickness || 0);
    return height && width && thickness
        ? `${height}×${width}×${thickness}`
        : 'Размер не указан';
};

export const normalizeBlankSearchText = (value) => String(value ?? '')
    .toLocaleLowerCase('ru-RU')
    .replace(/[×xх*\/|,;:_–—-]+/giu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const buildBlankRuleSearchText = (rule = {}, suppliers = []) => {
    const supplierByKey = new Map(
        (Array.isArray(suppliers) ? suppliers : [])
            .map((supplier) => [normalizeSupplierKey(supplier?.key), supplier])
    );
    const offers = (Array.isArray(rule.supplierOffers) ? rule.supplierOffers : [])
        .map((offer) => {
            const supplierKey = normalizeSupplierKey(offer?.supplierKey);
            const supplier = supplierByKey.get(supplierKey);
            return [
                supplierKey,
                supplier?.name,
                offer?.price,
                offer?.note,
            ].filter(Boolean).join(' ');
        })
        .join(' ');
    const height = Number(rule.height || 0);
    const width = Number(rule.width || 0);
    const thickness = Number(rule.thickness || 0);

    return normalizeBlankSearchText([
        rule.label,
        rule.note,
        rule.blankType,
        getBlankTypeLabel(rule.blankType),
        rule.polishingType,
        getPolishingLabel(rule.polishingType),
        rule.priceUnit,
        getPriceUnitLabel(rule.priceUnit),
        `${height} ${width} ${thickness}`,
        `${height}x${width}x${thickness}`,
        rule.price,
        rule.manualPrice,
        offers,
    ].filter(Boolean).join(' '));
};

export const matchesBlankRuleSearch = (rule = {}, searchValue = '', suppliers = []) => {
    const tokens = normalizeBlankSearchText(searchValue).split(' ').filter(Boolean);
    if (!tokens.length) return true;

    const haystack = buildBlankRuleSearchText(rule, suppliers);
    return tokens.every((token) => haystack.includes(token));
};

export const getOptionLabel = (options, value, fallback = value || '—') => (
    options.find((item) => item.value === value)?.label || fallback
);

export const getBlankTypeLabel = (value) => (
    getOptionLabel(BLANK_TYPE_OPTIONS, value, value || 'Заготовка')
);

export const getPolishingLabel = (value) => (
    getOptionLabel(POLISHING_OPTIONS, value, value || '—')
);

export const getPriceUnitLabel = (value) => (
    getOptionLabel(PRICE_UNIT_OPTIONS, value, value || 'за штуку')
);

export const normalizeSupplierKey = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я0-9]+/giu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export const normalizeOffer = (offer = {}) => ({
    supplierKey: normalizeSupplierKey(offer.supplierKey),
    price: toNumber(offer.price, 0),
    enabled: offer.enabled !== false,
    confirmed: offer.confirmed !== false,
    sourceDate: offer.sourceDate || null,
    importedAt: offer.importedAt || null,
    sourceVolumeM3: toNumber(offer.sourceVolumeM3, 0),
    sourceMassKg: toNumber(offer.sourceMassKg, 0),
    note: String(offer.note || ''),
});

export const resolveWorkingOffer = (rule = {}) => {
    if (rule?.priceSource === 'material-square-meter') {
        return {
            price: 0,
            supplierKey: '',
            source: 'material-square-meter',
        };
    }

    const offers = (Array.isArray(rule.supplierOffers) ? rule.supplierOffers : [])
        .map(normalizeOffer)
        .filter((offer) => (
            offer.supplierKey
            && offer.enabled !== false
            && offer.confirmed !== false
            && Number(offer.price || 0) > 0
        ));

    if (rule.priceSelectionMode === 'manual' && toNumber(rule.manualPrice, 0) > 0) {
        return {
            price: toNumber(rule.manualPrice, 0),
            supplierKey: '',
            source: 'manual',
        };
    }

    if (rule.priceSelectionMode === 'supplier' && rule.selectedSupplierKey) {
        const selected = offers.find((offer) => (
            offer.supplierKey === normalizeSupplierKey(rule.selectedSupplierKey)
        ));
        if (selected) {
            return {
                price: selected.price,
                supplierKey: selected.supplierKey,
                source: 'selected-supplier',
            };
        }
        return {
            price: 0,
            supplierKey: normalizeSupplierKey(rule.selectedSupplierKey),
            source: 'selected-supplier-missing',
        };
    }

    const cheapest = [...offers].sort((left, right) => left.price - right.price)[0];
    if (cheapest) {
        return {
            price: cheapest.price,
            supplierKey: cheapest.supplierKey,
            source: 'cheapest-confirmed',
        };
    }

    if (toNumber(rule.manualPrice, 0) > 0) {
        return {
            price: toNumber(rule.manualPrice, 0),
            supplierKey: '',
            source: 'manual-fallback',
        };
    }

    return {
        price: toNumber(rule.price, 0),
        supplierKey: toNumber(rule.price, 0) > 0 ? 'legacy' : '',
        source: toNumber(rule.price, 0) > 0 ? 'legacy' : 'missing',
    };
};

export const normalizeRuleForEditor = (rule = {}, index = 0) => {
    const supplierOffers = (Array.isArray(rule.supplierOffers) ? rule.supplierOffers : [])
        .map(normalizeOffer)
        .filter((offer) => offer.supplierKey);
    const legacyPrice = toNumber(rule.price, 0);
    const hasOffers = supplierOffers.length > 0;
    const manualPrice = toNumber(rule.manualPrice, hasOffers ? 0 : legacyPrice);
    const priceSelectionMode = ['cheapest-confirmed', 'supplier', 'manual']
        .includes(rule.priceSelectionMode)
        ? rule.priceSelectionMode
        : hasOffers ? 'cheapest-confirmed' : legacyPrice > 0 ? 'manual' : 'cheapest-confirmed';

    const normalized = {
        ...rule,
        _clientId: rule._id || rule._clientId || `${createClientId()}-${index}`,
        blankType: rule.blankType || 'stele',
        polishingType: rule.polishingType || 'two-side',
        priceUnit: rule.priceUnit || 'piece',
        priceSource: rule.priceSource === 'material-square-meter' ? 'material-square-meter' : 'offers',
        height: toNumber(rule.height, 0),
        width: toNumber(rule.width, 0),
        thickness: toNumber(rule.thickness, 0),
        minAreaM2: toNumber(rule.minAreaM2, 0),
        maxAreaM2: toNumber(rule.maxAreaM2, 0),
        manualPrice,
        priceSelectionMode,
        selectedSupplierKey: normalizeSupplierKey(rule.selectedSupplierKey),
        supplierOffers,
        includesPolishedChamfer: rule.includesPolishedChamfer === true,
        favorite: rule.favorite === true,
        enabled: rule.enabled !== false,
        label: String(rule.label || ''),
        note: String(rule.note || ''),
    };
    const working = resolveWorkingOffer(normalized);
    normalized.price = working.price;
    return normalized;
};

export const createBlankRule = (supplierKey = '') => ({
    _clientId: createClientId(),
    label: '',
    blankType: 'stele',
    polishingType: 'two-side',
    priceUnit: 'piece',
    priceSource: 'offers',
    height: 1000,
    width: 500,
    thickness: 80,
    minAreaM2: 0,
    maxAreaM2: 0,
    price: 0,
    manualPrice: 0,
    priceSelectionMode: supplierKey ? 'supplier' : 'manual',
    selectedSupplierKey: supplierKey,
    supplierOffers: supplierKey ? [{
        supplierKey,
        price: 0,
        enabled: true,
        confirmed: true,
        sourceDate: null,
        importedAt: null,
        sourceVolumeM3: 0,
        sourceMassKg: 0,
        note: '',
    }] : [],
    includesPolishedChamfer: false,
    favorite: false,
    enabled: true,
    note: '',
});

export const updateRuleOffer = (rule = {}, supplierKey, patch = {}) => {
    const normalizedKey = normalizeSupplierKey(supplierKey);
    const offers = (Array.isArray(rule.supplierOffers) ? rule.supplierOffers : [])
        .map(normalizeOffer);
    const index = offers.findIndex((offer) => offer.supplierKey === normalizedKey);
    const nextOffer = normalizeOffer({
        ...(index >= 0 ? offers[index] : {
            supplierKey: normalizedKey,
            enabled: true,
            confirmed: true,
        }),
        ...patch,
        supplierKey: normalizedKey,
    });

    if (index >= 0) offers[index] = nextOffer;
    else offers.push(nextOffer);

    const nextRule = {
        ...rule,
        supplierOffers: offers,
    };
    const working = resolveWorkingOffer(nextRule);
    return { ...nextRule, price: working.price };
};

export const removeRuleOffer = (rule = {}, supplierKey) => {
    const normalizedKey = normalizeSupplierKey(supplierKey);
    const nextRule = {
        ...rule,
        supplierOffers: (Array.isArray(rule.supplierOffers) ? rule.supplierOffers : [])
            .filter((offer) => normalizeSupplierKey(offer.supplierKey) !== normalizedKey),
        selectedSupplierKey: normalizeSupplierKey(rule.selectedSupplierKey) === normalizedKey
            ? ''
            : rule.selectedSupplierKey,
        priceSelectionMode: (
            rule.priceSelectionMode === 'supplier'
            && normalizeSupplierKey(rule.selectedSupplierKey) === normalizedKey
        ) ? 'cheapest-confirmed' : rule.priceSelectionMode,
    };
    const working = resolveWorkingOffer(nextRule);
    return { ...nextRule, price: working.price };
};

export const sortRules = (rules = []) => [...rules].sort((left, right) => (
    Number(right.favorite === true) - Number(left.favorite === true)
    || String(left.blankType || '').localeCompare(String(right.blankType || ''), 'ru')
    || Number(left.height || 0) - Number(right.height || 0)
    || Number(left.width || 0) - Number(right.width || 0)
    || Number(left.thickness || 0) - Number(right.thickness || 0)
    || String(left.polishingType || '').localeCompare(String(right.polishingType || ''), 'ru')
));

export const validateSupplierImportObject = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('JSON должен содержать один объект поставщика.');
    }
    if (!value.supplier || typeof value.supplier !== 'object') {
        throw new Error('В JSON отсутствует объект supplier.');
    }
    if (!Array.isArray(value.records)) {
        throw new Error('В JSON отсутствует массив records.');
    }
    if (!value.records.length) {
        throw new Error('В records нет строк для импорта.');
    }
    return value;
};

export const readSupplierImportFile = async (file) => {
    if (!file) throw new Error('Файл не выбран.');
    if (file.size > 10 * 1024 * 1024) {
        throw new Error('JSON больше 10 МБ. Раздели прайс на несколько файлов.');
    }
    const text = await file.text();
    let parsed;
    try {
        parsed = JSON.parse(text);
    } catch (error) {
        throw new Error('Не удалось прочитать JSON. Проверь запятые и кавычки.');
    }
    return validateSupplierImportObject(parsed);
};

export const downloadJson = (value, fileName = 'blank-supplier.json') => {
    const blob = new Blob([JSON.stringify(value, null, 2)], {
        type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};
